import { useEffect, useState } from 'react';
import { Alert, Button } from 'react-bootstrap';
import parse from "html-react-parser";
import { registerNlpStart } from '../../utils/utils';
import { useTranslation } from 'react-i18next';

export interface AnalyzedTextProps {
    task_id: string;
    analyzedText: any;
    saveFormattedTextCallback: CallableFunction;
    language?: string;
}

interface FilterItem {
    id: string;
    category: string;
    option: string;
    color: string;
    size: string;
    bold: boolean;
    italic: boolean;
    underline: boolean;
}

export default function AnalyzedTextSpanish(props: AnalyzedTextProps) {
    const { t } = useTranslation();

    const [allText, setAllText] = useState('');
    const [processingEmpty, setProcessingEmpty] = useState(false);
    const [nlpCategories, setNlpCategories] = useState<any>({});
    const [filters, setFilters] = useState<FilterItem[]>([]);
    const [showPicker, setShowPicker] = useState(false);
    const [expandedPickerGroup, setExpandedPickerGroup] = useState<string | null>(null);

    // --- Color/Size decode ---

    function decodeColor(colorcode: string) {
        switch (colorcode) {
            case 'blue-violet': return 'blueviolet';
            case 'brown': return 'brown';
            case 'coral': return 'coral';
            case 'dark-blue': return 'darkblue';
            case 'dark-cyan': return 'darkcyan';
            case 'crimson': return 'crimson';
            case 'dark-green': return 'darkgreen';
            case 'dark-salmon': return 'darksalmon';
            case 'dark-orange': return 'darkorange';
            case 'cadet-blue': return 'cadetblue';
            case 'chocolate': return 'chocolate';
            case 'dark-magenta': return 'darkmagenta';
            default: return '';
        }
    }

    function decodeSize(sizecode: string) {
        switch (sizecode) {
            case 'big': return '1.4em';
            case 'huge': return '1.8em';
            default: return '';
        }
    }

    // --- Feature helper ---

    function hasFeature(token: any, featureKey: string, featureValue?: string): boolean {
        if (!token.features) return false;
        const features = token.features.split('|');
        if (featureValue) {
            return features.some((f: string) => f === `${featureKey}=${featureValue}`);
        } else {
            return features.some((f: string) => f.startsWith(`${featureKey}=`));
        }
    }

    // --- Word matching ---

    const wordMatchesFilter = (word: any, filter: FilterItem): boolean => {
        switch (filter.category) {
            case 'nouns':
                if (word.upos !== 'NOUN') return false;
                switch (filter.option) {
                    case 'feminine': return word.xpos === 'ncfs000' || word.xpos === 'ncfp000';
                    case 'masculine': return word.xpos === 'ncms000' || word.xpos === 'ncmp000';
                    case 'common': return word.xpos === 'nccs000' || word.xpos === 'nccp000';
                    case 'invariable': return word.xpos === 'ncmn000' || word.xpos === 'ncfn000' || word.xpos === 'nccn000';
                    case 'all':
                    default: return true;
                }

            case 'adjectives':
                if (word.upos !== 'ADJ') return false;
                switch (filter.option) {
                    case 'feminine': return word.xpos === 'AQ0FP0' || word.xpos === 'AQ0FS0';
                    case 'masculine': return word.xpos === 'AQ0MP0' || word.xpos === 'AQ0MS0';
                    case 'common': return word.xpos === 'AQ0CP0' || word.xpos === 'AQ0CS0';
                    case 'invariable': return word.xpos === 'AQ0CN0';
                    case 'all':
                    default: return true;
                }

            case 'adverbs':
                return word.upos === 'RG' || word.upos === 'RN';

            case 'verbs':
                return word.upos === 'VERB';

            case 'prepositions':
                return word.upos === 'ADP';

            case 'subConjunctions':
                return word.upos === 'SCONJ';

            case 'determiners':
                if (word.upos !== 'DET') return false;
                switch (filter.option) {
                    case 'articles': return hasFeature(word, 'PronType', 'Art');
                    case 'demonstrative': return hasFeature(word, 'PronType', 'Dem');
                    case 'possessive': return hasFeature(word, 'PronType', 'Pos');
                    case 'indefinite': return hasFeature(word, 'PronType', 'Ind');
                    case 'interrogative': return hasFeature(word, 'PronType', 'Int');
                    case 'relative': return hasFeature(word, 'PronType', 'Rel');
                    case 'numeral': return hasFeature(word, 'PronType', 'Num');
                    case 'exclamatory': return hasFeature(word, 'PronType', 'Excl');
                    case 'all':
                    default: return true;
                }

            case 'pronouns':
                if (word.upos !== 'PRON') return false;
                switch (filter.option) {
                    case 'possessive': return hasFeature(word, 'Poss', 'Yes');
                    case 'demonstrative': return hasFeature(word, 'PronType', 'Dem');
                    case 'relative': return hasFeature(word, 'PronType', 'Rel');
                    case 'all':
                    default: return true;
                }

            case 'contractions':
                return word.features === 'Contraction';

            case 'conjunctions':
                return word.upos === 'CCONJ';

            default:
                return false;
        }
    }

    // --- Format text ---

    const formatText = (json: any, currentFilters: FilterItem[]) => {
        let resultAllText = '';
        if (json && json.length > 0) {
            let paragraphNum = json[0].paragraph;
            let sentenceNum = json[0].sentence;
            resultAllText = '<p>';
            let prevEndChar = 0;

            for (let i = 0; i < json.length; i++) {
                if (json[i].start_char === '-') continue;
                if (parseInt(json[i].sentence) != parseInt(sentenceNum)) {
                    resultAllText += ' ';
                    sentenceNum = json[i].sentence;
                }
                if (parseInt(json[i].paragraph) != parseInt(paragraphNum)) {
                    resultAllText += '</p><p>';
                    paragraphNum = json[i].paragraph;
                }
                if (prevEndChar != 0 && (prevEndChar < parseInt(json[i].start_char))) {
                    resultAllText += ' ';
                }

                let matchingFilter: FilterItem | null = null;
                for (const filter of currentFilters) {
                    if (wordMatchesFilter(json[i], filter)) {
                        matchingFilter = filter;
                    }
                }

                if (matchingFilter) {
                    let style = '';
                    let classProps = '';
                    if (matchingFilter.color) {
                        style += 'color:' + decodeColor(matchingFilter.color) + ';';
                    }
                    if (matchingFilter.size) {
                        style += 'font-size:' + decodeSize(matchingFilter.size) + ';';
                        classProps = 'text-' + matchingFilter.size;
                    }
                    if (matchingFilter.bold) {
                        style += 'font-weight: bold;';
                    }
                    if (matchingFilter.italic) {
                        style += 'font-style: italic;';
                    }
                    if (matchingFilter.underline) {
                        style += 'text-decoration: underline;';
                    }
                    resultAllText += '<span class="' + classProps + '" style="' + style + '">' + json[i].word + '</span>';
                } else {
                    resultAllText += json[i].word;
                }

                prevEndChar = parseInt(json[i].end_char);
            }
            resultAllText += '</p>';
        }
        setAllText(resultAllText);
        props.saveFormattedTextCallback(resultAllText);
    }

    // --- Filter CRUD ---

    const addFilter = (category: string, option: string) => {
        const newFilter: FilterItem = {
            id: Date.now().toString() + Math.random().toString(36).substr(2, 5),
            category,
            option,
            color: '',
            size: '',
            bold: false,
            italic: false,
            underline: false,
        };
        const newFilters = [...filters, newFilter];
        setFilters(newFilters);
        setShowPicker(false);
        setExpandedPickerGroup(null);
        registerNlpStart(props.task_id, 'Word Categories', category, option);
        formatText(props.analyzedText.main, newFilters);
    };

    const removeFilter = (id: string) => {
        const newFilters = filters.filter(f => f.id !== id);
        setFilters(newFilters);
        formatText(props.analyzedText.main, newFilters);
    };

    const updateFilter = (id: string, updates: Partial<FilterItem>) => {
        const newFilters = filters.map(f => f.id === id ? { ...f, ...updates } : f);
        setFilters(newFilters);
        formatText(props.analyzedText.main, newFilters);
    };

    const handleColorChange = (filterId: string, color: string) => {
        const filter = filters.find(f => f.id === filterId);
        if (filter) {
            registerNlpStart(props.task_id, 'Color', filter.category, color);
        }
        updateFilter(filterId, { color });
    };

    const handleSizeChange = (filterId: string, size: string) => {
        const filter = filters.find(f => f.id === filterId);
        if (filter) {
            registerNlpStart(props.task_id, 'Size', filter.category, size);
        }
        updateFilter(filterId, { size });
    };

    const handleBoldToggle = (filterId: string) => {
        const filter = filters.find(f => f.id === filterId);
        if (filter) {
            registerNlpStart(props.task_id, 'Bold', filter.category, !filter.bold ? 'yes' : 'no');
            updateFilter(filterId, { bold: !filter.bold });
        }
    };

    const handleItalicToggle = (filterId: string) => {
        const filter = filters.find(f => f.id === filterId);
        if (filter) {
            registerNlpStart(props.task_id, 'Italic', filter.category, !filter.italic ? 'yes' : 'no');
            updateFilter(filterId, { italic: !filter.italic });
        }
    };

    const handleUnderlineToggle = (filterId: string) => {
        const filter = filters.find(f => f.id === filterId);
        if (filter) {
            registerNlpStart(props.task_id, 'Underline', filter.category, !filter.underline ? 'yes' : 'no');
            updateFilter(filterId, { underline: !filter.underline });
        }
    };

    // --- Render helpers ---

    const colorOptions = (filterId: string, activeColor: string) => {
        const colors = ['blue-violet', 'brown', 'coral', 'dark-blue', 'dark-cyan', 'crimson',
            'dark-green', 'dark-salmon', 'dark-orange', 'cadet-blue', 'chocolate', 'dark-magenta'];
        return (
            <div className="color-selector">
                {colors.map(color => (
                    <span key={color}
                        className={'color-item ' + color + (activeColor === color ? ' active' : '')}
                        onClick={() => handleColorChange(filterId, color)}></span>
                ))}
            </div>
        );
    };

    const sizeOptions = (filterId: string, activeSize: string) => {
        return (
            <div className="size-selector">
                <span className={'size-item normal' + (activeSize === 'normal' ? ' active' : '')}
                    onClick={() => handleSizeChange(filterId, 'normal')}>{'A'}</span>
                <span className={'size-item big' + (activeSize === 'big' ? ' active' : '')}
                    onClick={() => handleSizeChange(filterId, 'big')}>{'A'}</span>
                <span className={'size-item huge' + (activeSize === 'huge' ? ' active' : '')}
                    onClick={() => handleSizeChange(filterId, 'huge')}>{'A'}</span>
            </div>
        );
    };

    const getFilterLabel = (category: string, option: string): string => {
        const labels: Record<string, Record<string, string>> = {
            nouns: {
                'all': t("All nouns"),
                'feminine': t("Feminine"),
                'masculine': t("Masculine"),
                'common': t("Common"),
                'invariable': t("Invariable"),
            },
            adjectives: {
                'all': t("All adjectives"),
                'feminine': t("Feminine"),
                'masculine': t("Masculine"),
                'common': t("Common"),
                'invariable': t("Invariable"),
            },
            adverbs: {
                'all': t("All adverbs"),
            },
            verbs: {
                'all': t("All verbs"),
            },
            prepositions: {
                'all': t("Prepositions"),
            },
            subConjunctions: {
                'all': t("Subordinating Conjunctions"),
            },
            determiners: {
                'all': t("All determiners"),
                'articles': t("Articles"),
                'demonstrative': t("Demonstrative"),
                'possessive': t("Possessive"),
                'indefinite': t("Indefinite"),
                'interrogative': t("Interrogative"),
                'relative': t("Relative"),
                'numeral': t("Numeral"),
                'exclamatory': t("Exclamatory"),
            },
            pronouns: {
                'all': t("All pronouns"),
                'possessive': t("Possessive"),
                'demonstrative': t("Demonstrative"),
                'relative': t("Relative"),
            },
            contractions: {
                'all': t("Contractions"),
            },
            conjunctions: {
                'all': t("Coordinating Conjunctions"),
            },
        };
        const groupNames: Record<string, string> = {
            nouns: t("Nouns"),
            adjectives: t("Adjectives"),
            adverbs: t("Adverbs"),
            verbs: t("Verbs"),
            determiners: t("Determiners"),
            pronouns: t("Pronouns"),
        };
        const label = labels[category]?.[option] || option;
        if (option !== 'all' && groupNames[category]) {
            return groupNames[category] + ' › ' + label;
        }
        return label;
    };

    const getPickerGroups = () => [
        {
            key: 'nouns',
            label: t("Nouns"),
            options: [
                { value: 'all', label: t("All nouns"), count: nlpCategories.nounsAll },
                { value: 'feminine', label: t("Feminine"), count: nlpCategories.nounsFeminine },
                { value: 'masculine', label: t("Masculine"), count: nlpCategories.nounsMasculine },
                { value: 'common', label: t("Common"), count: nlpCategories.nounsCommon },
                { value: 'invariable', label: t("Invariable"), count: nlpCategories.nounsInvariable },
            ]
        },
        {
            key: 'adjectives',
            label: t("Adjectives"),
            options: [
                { value: 'all', label: t("All adjectives"), count: nlpCategories.adjectivesAll },
                { value: 'feminine', label: t("Feminine"), count: nlpCategories.adjectivesFeminine },
                { value: 'masculine', label: t("Masculine"), count: nlpCategories.adjectivesMasculine },
                { value: 'common', label: t("Common"), count: nlpCategories.adjectivesCommon },
                { value: 'invariable', label: t("Invariable"), count: nlpCategories.adjectivesInvariable },
            ]
        },
        {
            key: 'adverbs',
            label: t("Adverbs"),
            options: [
                { value: 'all', label: t("All adverbs"), count: nlpCategories.adverbsAll },
            ]
        },
        {
            key: 'verbs',
            label: t("Verbs"),
            options: [
                { value: 'all', label: t("All verbs"), count: nlpCategories.verbsAll },
            ]
        },
        {
            key: 'prepositions',
            label: t("Prepositions"),
            options: [
                { value: 'all', label: t("Prepositions"), count: nlpCategories.prepositionsAll },
            ]
        },
        {
            key: 'subConjunctions',
            label: t("Subordinating Conjunctions"),
            options: [
                { value: 'all', label: t("Subordinating Conjunctions"), count: nlpCategories.subConjunctionsAll },
            ]
        },
        {
            key: 'determiners',
            label: t("Determiners"),
            options: [
                { value: 'all', label: t("All determiners"), count: nlpCategories.determinersAll },
                { value: 'articles', label: t("Articles"), count: nlpCategories.determinersArticles },
                { value: 'demonstrative', label: t("Demonstrative"), count: nlpCategories.determinersDemonstrative },
                { value: 'possessive', label: t("Possessive"), count: nlpCategories.determinersPossessive },
                { value: 'indefinite', label: t("Indefinite"), count: nlpCategories.determinersIndefinite },
                { value: 'interrogative', label: t("Interrogative"), count: nlpCategories.determinersInterrogative },
                { value: 'relative', label: t("Relative"), count: nlpCategories.determinersRelative },
                { value: 'numeral', label: t("Numeral"), count: nlpCategories.determinersNumeral },
                { value: 'exclamatory', label: t("Exclamatory"), count: nlpCategories.determinersExclamatory },
            ]
        },
        {
            key: 'pronouns',
            label: t("Pronouns"),
            options: [
                { value: 'all', label: t("All pronouns"), count: nlpCategories.pronounsAll },
                { value: 'possessive', label: t("Possessive"), count: nlpCategories.pronounsPossessive },
                { value: 'demonstrative', label: t("Demonstrative"), count: nlpCategories.pronounsDemonstrative },
                { value: 'relative', label: t("Relative"), count: nlpCategories.pronounsRelative },
            ]
        },
        {
            key: 'contractions',
            label: t("Contractions"),
            options: [
                { value: 'all', label: t("Contractions"), count: nlpCategories.contractionsAll },
            ]
        },
        {
            key: 'conjunctions',
            label: t("Coordinating Conjunctions"),
            options: [
                { value: 'all', label: t("Coordinating Conjunctions"), count: nlpCategories.conjunctionsAll },
            ]
        },
    ];

    const renderFilterCard = (filter: FilterItem) => {
        return (
            <div className="word-category" key={filter.id}>
                <div className="word-category-switch active" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{getFilterLabel(filter.category, filter.option)}</span>
                    <span
                        onClick={() => removeFilter(filter.id)}
                        style={{ cursor: 'pointer', fontWeight: 'bold', fontSize: '16px', lineHeight: 1, padding: '0 4px' }}
                        title={t("Remove")}
                    >&times;</span>
                </div>
                <div className="options">
                    <div className="format-options">
                        {colorOptions(filter.id, filter.color)}
                        {sizeOptions(filter.id, filter.size)}
                        <div className="other-formats">
                            <span className={'bold' + (filter.bold ? ' active' : '')}
                                onClick={() => handleBoldToggle(filter.id)}></span>
                            <span className={'italic' + (filter.italic ? ' active' : '')}
                                onClick={() => handleItalicToggle(filter.id)}></span>
                            <span className={'underline' + (filter.underline ? ' active' : '')}
                                onClick={() => handleUnderlineToggle(filter.id)}></span>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderFilterPicker = () => {
        const groups = getPickerGroups();
        return (
            <div className="filter-picker">
                {groups.map((group: any) => (
                    <div key={group.key} className="filter-picker-group">
                        <div
                            className={'filter-picker-group-header' + (expandedPickerGroup === group.key ? ' expanded' : '')}
                            onClick={() => setExpandedPickerGroup(expandedPickerGroup === group.key ? null : group.key)}
                        >
                            <span className="filter-picker-arrow">{expandedPickerGroup === group.key ? '\u25BC' : '\u25B6'}</span>
                            {group.label}
                        </div>
                        {expandedPickerGroup === group.key &&
                            <div className="filter-picker-options">
                                {group.options.map((opt: any) => (
                                    <div
                                        key={opt.value}
                                        className="filter-picker-option"
                                        onClick={() => addFilter(group.key, opt.value)}
                                    >
                                        {opt.label} ({opt.count || 0})
                                    </div>
                                ))}
                            </div>
                        }
                    </div>
                ))}
            </div>
        );
    };

    // --- useEffect for NLP categories ---

    useEffect(() => {
        if (props.analyzedText.main && props.analyzedText.main.length == 0) {
            setProcessingEmpty(true);
        } else {
            setProcessingEmpty(false);
            const newNlpCategories = {
                // Nouns - using UPOS
                nounsAll: props.analyzedText.main.filter((c: any) => c.upos === 'NOUN').length,
                nounsFeminine: props.analyzedText.main.filter((c: any) => c.upos === 'NOUN' && (c.xpos === 'ncfs000' || c.xpos === 'ncfp000')).length,
                nounsMasculine: props.analyzedText.main.filter((c: any) => c.upos === 'NOUN' && (c.xpos === 'ncms000' || c.xpos === 'ncmp000')).length,
                nounsCommon: props.analyzedText.main.filter((c: any) => c.upos === 'NOUN' && (c.xpos === 'nccs000' || c.xpos === 'nccp000')).length,
                nounsInvariable: props.analyzedText.main.filter((c: any) => c.upos === 'NOUN' && (c.xpos === 'ncmn000' || c.xpos === 'ncfn000' || c.xpos === 'nccn000')).length,

                // Adjectives - using UPOS
                adjectivesAll: props.analyzedText.main.filter((c: any) => c.upos === 'ADJ').length,
                adjectivesFeminine: props.analyzedText.main.filter((c: any) => c.upos === 'ADJ' && (c.xpos === 'AQ0FP0' || c.xpos === 'AQ0FS0')).length,
                adjectivesMasculine: props.analyzedText.main.filter((c: any) => c.upos === 'ADJ' && (c.xpos === 'AQ0MP0' || c.xpos === 'AQ0MS0')).length,
                adjectivesCommon: props.analyzedText.main.filter((c: any) => c.upos === 'ADJ' && (c.xpos === 'AQ0CP0' || c.xpos === 'AQ0CS0')).length,
                adjectivesInvariable: props.analyzedText.main.filter((c: any) => c.upos === 'ADJ' && c.xpos === 'AQ0CN0').length,

                // Adverbs - using UPOS (no subcategories for Spanish)
                adverbsAll: props.analyzedText.main.filter((c: any) => c.upos === 'RG' || c.upos === 'RN').length,

                // Verbs - using UPOS (no irregular category for now)
                verbsAll: props.analyzedText.main.filter((c: any) => c.upos === 'VERB').length,

                // Prepositions
                prepositionsAll: props.analyzedText.main.filter((c: any) => c.upos === 'ADP').length,

                // Subordinating Conjunctions
                subConjunctionsAll: props.analyzedText.main.filter((c: any) => c.upos === 'SCONJ').length,

                // Determiners with subcategories based on features
                determinersAll: props.analyzedText.main.filter((c: any) => c.upos === 'DET').length,
                determinersArticles: props.analyzedText.main.filter((c: any) => c.upos === 'DET' && hasFeature(c, 'PronType', 'Art')).length,
                determinersDemonstrative: props.analyzedText.main.filter((c: any) => c.upos === 'DET' && hasFeature(c, 'PronType', 'Dem')).length,
                determinersPossessive: props.analyzedText.main.filter((c: any) => c.upos === 'DET' && hasFeature(c, 'PronType', 'Pos')).length,
                determinersIndefinite: props.analyzedText.main.filter((c: any) => c.upos === 'DET' && hasFeature(c, 'PronType', 'Ind')).length,
                determinersInterrogative: props.analyzedText.main.filter((c: any) => c.upos === 'DET' && hasFeature(c, 'PronType', 'Int')).length,
                determinersRelative: props.analyzedText.main.filter((c: any) => c.upos === 'DET' && hasFeature(c, 'PronType', 'Rel')).length,
                determinersNumeral: props.analyzedText.main.filter((c: any) => c.upos === 'DET' && hasFeature(c, 'PronType', 'Num')).length,
                determinersExclamatory: props.analyzedText.main.filter((c: any) => c.upos === 'DET' && hasFeature(c, 'PronType', 'Excl')).length,

                // Pronouns with subcategories based on features
                pronounsAll: props.analyzedText.main.filter((c: any) => c.upos === 'PRON').length,
                pronounsPossessive: props.analyzedText.main.filter((c: any) => c.upos === 'PRON' && hasFeature(c, 'Poss', 'Yes')).length,
                pronounsDemonstrative: props.analyzedText.main.filter((c: any) => c.upos === 'PRON' && hasFeature(c, 'PronType', 'Dem')).length,
                pronounsRelative: props.analyzedText.main.filter((c: any) => c.upos === 'PRON' && hasFeature(c, 'PronType', 'Rel')).length,

                // Contractions
                contractionsAll: props.analyzedText.main.filter((c: any) => c.features === 'Contraction').length,

                // Coordinating Conjunctions
                conjunctionsAll: props.analyzedText.main.filter((c: any) => c.upos === 'CCONJ').length,
            };
            setNlpCategories(newNlpCategories);
            formatText(props.analyzedText.main, []);
        }
    }, []);

    // --- Render ---

    return (
        <div className="processed-text-container">
            { processingEmpty &&
                <div className="empty-processed-text">
                    <Alert key={ 'warning' } variant={ 'warning' }>
                        { t("Sorry! The result of the text analysis is either empty or some errors were found. Revise and change your text and try it again.") }
                    </Alert>
                </div>
            }
            { !processingEmpty &&
                <div className="text-filters">
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={() => { setShowPicker(!showPicker); setExpandedPickerGroup(null); }}
                        style={{ width: '100%', marginBottom: '10px' }}
                    >
                        { showPicker ? t("Cancel") : '+ ' + t("Add formatting rule") }
                    </Button>

                    { showPicker && renderFilterPicker() }

                    { filters.map(filter => renderFilterCard(filter)) }
                </div>
            }
            <div className="text-presentation">
                <div className="text-formatted">
                    { parse(allText)}
                </div>
            </div>
        </div>
    );
}
